import Plugin from '@ckeditor/ckeditor5-core/src/plugin';
import BalloonEditor from '@ckeditor/ckeditor5-editor-balloon/src/ballooneditor';

import TrackChanges from '@ckeditor/ckeditor5-track-changes/src/trackchanges';
import TrackChangesData from '@ckeditor/ckeditor5-track-changes/src/trackchangesdata';
import Comments from '@ckeditor/ckeditor5-comments/src/comments';

export default class PlainData extends Plugin {

	/**
	 * @inheritDoc
	 */
	constructor( editor ) {
		super( editor );

		const config = editor.config.get( 'plainData' ) || {};

		// A minimum amount of time that needs to pass after the last action.
		// After that time the provided save callbacks are being called.
		this.acceptAllChanges = config.acceptAllChanges || true;

		/**
		 * The configuration of this plugins.
		 *
		 * @private
		 * @type {Object}
		 */
		this._config = config;
	}

	static get pluginName() {
		return 'PlainData';
	}

	static get requires() {
		return [ TrackChanges, TrackChangesData, Comments ];
	}

	/**
	 * (`config.plainData`), which:
	 *
	 * * may be defined as an `Object`:
	 *
	 *		plainData: {
	 *		    acceptAllChanges: boolean if all chages should be accepted. Default is true
	 *		}
	 */
	init() {
		const editor = this.editor;
		editor.getPlainData = this.getData.bind( this );
	}

	getData( acceptAll = this.acceptAllChanges ) {
		const element = document.createElement( 'div' );
		const sourceEditor = this.editor;
		const editorConfig = this._createEditorConfig( sourceEditor.config );
		return BalloonEditor.create( element, editorConfig )
			.then( editor => {
				this._copyUser( sourceEditor, editor );
				this._copyComments( sourceEditor, editor );
				this._copySuggestions( sourceEditor, editor );
				editor.setData(sourceEditor.getData());
				return editor;
			})
			.then(editor => {
				this.removeAllComments(editor);
				return editor;
			})
			.then(editor => this.handleSuggestions(editor, acceptAll)
					.then(data => new Promise(resolve => editor.destroy().then(resolve(data))))
			);
	}

	/**
	 * remove autosave, extraPlugins and more
	 * @param config
	 * @private
	 */
	_createEditorConfig( config ) {
		const configEntryToCopy = [ 'blockToolbar', 'toolbar', 'balloonToolbar', 'image', 'table', 'language',
			'licenseKey', 'heading', 'placeholder', 'deleteButton', 'plugins', 'alignment', 'comments.editorConfig', 'list'];
		const editorConfig = {};
		for (const configEntry of configEntryToCopy) {
			let configValue = config.get( configEntry );
			if (configValue) {
				editorConfig[configEntry] = configValue;
			}
		}
		editorConfig['comments'] = {};
		editorConfig['comments']['editorConfig'] = config.get('comments.editorConfig')
		return editorConfig;
	}

	_copySuggestions( sourceEditor, editor ) {
		const sourceTrackChangesPlugin = sourceEditor.plugins.get( TrackChanges.pluginName );
		const trackChangesPlugin = editor.plugins.get( TrackChanges.pluginName );
		const options = {
			skipNotAttached: true,
			toJSON: true
		};
		for (const suggestion of sourceTrackChangesPlugin.getSuggestions(options)) {
			trackChangesPlugin.addSuggestion( suggestion );
		}
	}

	_copyUser( sourceEditor, editor ) {
		const sourceUsersPlugin = sourceEditor.plugins.get( 'Users' );
		const usersPlugin = editor.plugins.get( 'Users' );
		const sourceUsers = sourceUsersPlugin.users.filter(user => !user.isAnonymous);
		for (const user of sourceUsers) {
			usersPlugin.addUser( {
				id: user.id,
				name: user.name
			} );
		}
		usersPlugin.defineMe( sourceUsersPlugin.me.id );
	}

	_copyComments( sourceEditor, editor ) {
		const sourceCommentsRepository = sourceEditor.plugins.get( 'CommentsRepository' );
		const commentsRepository = editor.plugins.get( 'CommentsRepository' );
		const options = {
			skipNotAttached: false,
			skipEmpty: false,
			toJSON: true
		};
		for (const commentThreadJson of sourceCommentsRepository.getCommentThreads(options)) {
			commentsRepository.addCommentThread( commentThreadJson );
		}
	}

	removeAllComments( editor ) {
		const commentsRepository = editor.plugins.get( 'CommentsRepository' );
		const options = {
			skipNotAttached: false,
			skipEmpty: false,
			toJSON: false
		};
		for (const commentThread of commentsRepository.getCommentThreads(options)) {
			commentThread.remove();
		}
	}

	handleSuggestions( editor, acceptAll ) {
		if (acceptAll) {
			return editor.plugins.get( TrackChangesData.pluginName ).getDataWithAcceptedSuggestions();
		} else {
			return editor.plugins.get( TrackChangesData.pluginName ).getDataWithDiscardedSuggestions();
		}
	}
}
